import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  People as PeopleIcon,
  Assignment as RequestIcon,
  Notifications as NoticeIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle,
} from '@mui/icons-material';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';

import DashboardHeader from './components/DashboardHeader';
import KPICard from './components/KPICard';
import ChartCard from './components/ChartCard';
import { useDateRange } from './hooks/useDateRange';
import { useDashboardData } from './hooks/useDashboardData';

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const {
    dateRange,
    dateRangeString,
    preset,
    setPreset,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
  } = useDateRange();

  const { data, loading, errors } = useDashboardData(
    dateRangeString.startDate,
    dateRangeString.endDate
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setRefreshKey((prev) => prev + 1);
    // Wait a bit for visual feedback
    setTimeout(() => setRefreshing(false), 500);
  }, []);

  // Prepare chart data
  const consentStatusData = data.dataPrincipals
    ? [
        { name: 'Active', value: data.dataPrincipals.activeConsent, color: theme.palette.success.main },
        { name: 'Withdrawn', value: data.dataPrincipals.withdrawnConsent, color: theme.palette.error.main },
        { name: 'Expired', value: data.dataPrincipals.expiredConsent, color: theme.palette.warning.main },
        { name: 'Unknown', value: data.dataPrincipals.unknownConsent, color: theme.palette.grey[500] },
      ].filter((item) => item.value > 0)
    : [];

  const requestStatusData = data.requests
    ? [
        { name: 'Open', value: data.requests.open, color: theme.palette.warning.main },
        { name: 'Closed', value: data.requests.closed, color: theme.palette.success.main },
      ]
    : [];

  // Mock trend data (would come from API in real implementation)
  const dpGrowthData = [
    { date: 'Day 1', count: 100 },
    { date: 'Day 2', count: 120 },
    { date: 'Day 3', count: 115 },
    { date: 'Day 4', count: 140 },
    { date: 'Day 5', count: 135 },
    { date: 'Day 6', count: 160 },
    { date: 'Day 7', count: 155 },
  ];

  const COLORS = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.error.main,
    theme.palette.info.main,
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
        Dashboard
      </Typography>

      <DashboardHeader
        preset={preset}
        onPresetChange={setPreset}
        customStartDate={customStartDate}
        onCustomStartDateChange={setCustomStartDate}
        customEndDate={customEndDate}
        onCustomEndDateChange={setCustomEndDate}
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      {/* Error Alerts */}
      {errors.dataPrincipals && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Data Principals: {errors.dataPrincipals}
        </Alert>
      )}
      {errors.requests && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Requests: {errors.requests}
        </Alert>
      )}

      {/* KPI Cards Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KPICard
            title="Total Data Principals"
            value={data.dataPrincipals?.total ?? null}
            icon={<PeopleIcon />}
            color="primary"
            loading={loading.dataPrincipals}
            onClick={() => navigate('/workbench/data-principals')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KPICard
            title="Active DPs"
            value={data.dataPrincipals?.active ?? null}
            subtitle={`of ${data.dataPrincipals?.total ?? 0} total`}
            icon={<ActiveIcon />}
            color="success"
            loading={loading.dataPrincipals}
            onClick={() => navigate('/workbench/data-principals?isActive=true')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KPICard
            title="Active Consents"
            value={data.dataPrincipals?.activeConsent ?? null}
            icon={<CheckCircle />}
            color="success"
            loading={loading.dataPrincipals}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KPICard
            title="Pending Requests"
            value={data.requests?.open ?? null}
            subtitle={data.requests?.urgent ? `${data.requests.urgent} urgent` : undefined}
            icon={<RequestIcon />}
            color={data.requests?.urgent ? 'error' : 'warning'}
            loading={loading.requests}
            onClick={() => navigate('/workbench/requests?status=PENDING,IN_PROGRESS')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KPICard
            title="Total Requests"
            value={data.requests?.total ?? null}
            subtitle={`${data.requests?.closed ?? 0} closed`}
            icon={<RequestIcon />}
            color="info"
            loading={loading.requests}
            onClick={() => navigate('/workbench/requests')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <KPICard
            title="Test DPs"
            value={data.dataPrincipals?.test ?? null}
            subtitle={`${data.dataPrincipals?.production ?? 0} production`}
            icon={<WarningIcon />}
            color="warning"
            loading={loading.dataPrincipals}
          />
        </Grid>
      </Grid>

      {/* Main Content Sections */}
      <Grid container spacing={3}>
        {/* Data Principals Section */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Data Principals Overview"
            subtitle="Consent status distribution"
            loading={loading.dataPrincipals}
          >
            {data.dataPrincipals && consentStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={consentStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ cx, cy, midAngle, innerRadius, outerRadius, name, percent }) => {
                      const RADIAN = Math.PI / 180;
                      const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      
                      return (
                        <text
                          x={x}
                          y={y}
                          fill={theme.palette.text.primary}
                          textAnchor={x > cx ? 'start' : 'end'}
                          dominantBaseline="central"
                          fontSize={12}
                        >
                          {`${name}: ${(percent * 100).toFixed(0)}%`}
                        </text>
                      );
                    }}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {consentStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: theme.shape.borderRadius,
                      color: theme.palette.text.primary,
                    }}
                    labelStyle={{ 
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                    }}
                    itemStyle={{ 
                      color: theme.palette.text.primary,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                <Typography color="text.secondary">No data available</Typography>
              </Box>
            )}
          </ChartCard>
        </Grid>

        {/* Rights Requests Section */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Rights Requests"
            subtitle="Open vs Closed"
            loading={loading.requests}
          >
            {data.requests && requestStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={requestStatusData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="name" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: theme.shape.borderRadius,
                      color: theme.palette.text.primary,
                    }}
                    labelStyle={{ 
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                    }}
                    itemStyle={{ 
                      color: theme.palette.text.primary,
                    }}
                  />
                  <Bar dataKey="value" fill={theme.palette.primary.main} radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                <Typography color="text.secondary">No data available</Typography>
              </Box>
            )}
          </ChartCard>
        </Grid>

        {/* Data Principal Growth Trend */}
        <Grid item xs={12}>
          <ChartCard
            title="Data Principal Growth"
            subtitle="Trend over selected period"
            loading={loading.dataPrincipals}
          >
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dpGrowthData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis dataKey="date" tick={{ fill: theme.palette.text.secondary }} />
                <YAxis tick={{ fill: theme.palette.text.secondary }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: theme.shape.borderRadius,
                    color: theme.palette.text.primary,
                  }}
                  labelStyle={{ 
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                  }}
                  itemStyle={{ 
                    color: theme.palette.text.primary,
                  }}
                />
                <Legend
                  wrapperStyle={{ color: theme.palette.text.primary }}
                  iconType="line"
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  dot={{ r: 4, fill: theme.palette.primary.main }}
                  activeDot={{ r: 6 }}
                  name="Data Principals"
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Additional Stats Cards */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Identifier Type Distribution"
            subtitle="Email vs Phone"
            loading={loading.dataPrincipals}
          >
            {data.dataPrincipals ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={[
                    { name: 'Email', value: data.dataPrincipals.email, color: theme.palette.primary.main },
                    { name: 'Phone', value: data.dataPrincipals.phone, color: theme.palette.info.main },
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="name" tick={{ fill: theme.palette.text.secondary }} />
                  <YAxis tick={{ fill: theme.palette.text.secondary }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: theme.shape.borderRadius,
                      color: theme.palette.text.primary,
                    }}
                    labelStyle={{ 
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                    }}
                    itemStyle={{ 
                      color: theme.palette.text.primary,
                    }}
                  />
                  <Bar dataKey="value" fill={theme.palette.primary.main} radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                <Typography color="text.secondary">No data available</Typography>
              </Box>
            )}
          </ChartCard>
        </Grid>

        {/* Test vs Production */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Test vs Production"
            subtitle="Data Principal distribution"
            loading={loading.dataPrincipals}
          >
            {data.dataPrincipals ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Production', value: data.dataPrincipals.production, color: theme.palette.success.main },
                      { name: 'Test', value: data.dataPrincipals.test, color: theme.palette.warning.main },
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ cx, cy, midAngle, innerRadius, outerRadius, name, percent }) => {
                      const RADIAN = Math.PI / 180;
                      const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      
                      return (
                        <text
                          x={x}
                          y={y}
                          fill={theme.palette.text.primary}
                          textAnchor={x > cx ? 'start' : 'end'}
                          dominantBaseline="central"
                          fontSize={12}
                        >
                          {`${name}: ${(percent * 100).toFixed(0)}%`}
                        </text>
                      );
                    }}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {[
                      { name: 'Production', value: data.dataPrincipals.production, color: theme.palette.success.main },
                      { name: 'Test', value: data.dataPrincipals.test, color: theme.palette.warning.main },
                    ].map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: theme.shape.borderRadius,
                      color: theme.palette.text.primary,
                    }}
                    labelStyle={{ 
                      color: theme.palette.text.primary,
                      fontWeight: 500,
                    }}
                    itemStyle={{ 
                      color: theme.palette.text.primary,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                <Typography color="text.secondary">No data available</Typography>
              </Box>
            )}
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
